/**
 * Map stock fields from promotion API product rows onto UI product objects.
 * API often sends availableBoxQty / box_quantity rather than a generic `stock`.
 */
export function mapPromotionApiStockFields(product = {}) {
  const parseQty = (value) => {
    if (value === undefined || value === null || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const boxQty = parseQty(
    product.availableBoxQty ??
      product.available_box_qty ??
      product.box_quantity ??
      product.boxQuantity ??
      product.box_stock ??
      product.boxStock
  );
  const pieceQty = parseQty(
    product.availablePieceQty ??
      product.available_piece_qty ??
      product.piece_quantity ??
      product.pieceQuantity ??
      product.piece_stock ??
      product.pieceStock
  );
  const genericStock = parseQty(
    product.stock ?? product.stock_qty ?? product.current_stock ?? product.available_stock
  );

  return {
    availableBoxQty: boxQty,
    available_box_qty: boxQty,
    availablePieceQty: pieceQty,
    available_piece_qty: pieceQty,
    box_quantity: boxQty,
    boxQuantity: boxQty,
    box_stock: boxQty,
    piece_quantity: pieceQty,
    pieceQuantity: pieceQty,
    piece_stock: pieceQty,
    // Prefer type-specific stock; avoid forcing 0 when only box/piece qty exists
    stock: genericStock ?? boxQty ?? pieceQty ?? 0,
  };
}

/**
 * Display stock for promotion product cards based on selected Box/Piece type.
 * For API product.type "Both", show box stock.
 */
export function getPromotionStockByType(product, selectedTypeHint) {
  if (!product) return 0;

  const currentType = selectedTypeHint || product.type || '';
  const normalized = String(currentType).trim().toLowerCase();
  const selectedType =
    normalized === 'piece'
      ? 'Piece'
      : normalized === 'box' || normalized === 'both'
        ? 'Box'
        : 'Box';

  const parseCandidate = (candidate) => {
    if (
      candidate === null ||
      candidate === undefined ||
      candidate === '' ||
      candidate === 'null' ||
      candidate === 'undefined'
    ) {
      return null;
    }
    const num = Number(candidate);
    return Number.isFinite(num) ? num : null;
  };

  const candidates =
    selectedType === 'Piece'
      ? [
          product.piece_stock,
          product.pieceStock,
          product.availablePieceQty,
          product.available_piece_qty,
          product.pieceQuantity,
          product.piece_quantity,
          product.piece_stock_qty,
          product.pieceStockQty,
          product.stockDisplay,
          product.stock,
          product.stock_qty,
          product.current_stock,
          product.available_stock,
        ]
      : [
          product.box_stock,
          product.boxStock,
          product.availableBoxQty,
          product.available_box_qty,
          product.boxQuantity,
          product.box_quantity,
          product.box_stock_qty,
          product.boxStockQty,
          product.stockDisplay,
          product.stock,
          product.stock_qty,
          product.current_stock,
          product.available_stock,
        ];

  for (const candidate of candidates) {
    const num = parseCandidate(candidate);
    if (num !== null) {
      return num;
    }
  }
  return 0;
}

/**
 * Stock for product-select popup: use API `type`.
 * Piece → piece_stock, Box/Both → box_stock.
 */
export function getProductSelectPopupStock(product) {
  if (!product) return 0;
  return getPromotionStockByType(product, product.type);
}
